import { apiSetCardPin, selectedCards } from '@shared-core/features/backend'
import { useEvent } from '@shared-core/hooks/use-event'
import { useI18N } from '@shared-core/hooks/use-i18n'
import { useAppSelector } from '@shared-core/store'
import { AppBankCard } from '@shared-ui/components/app/bank-card'
import { Column } from '@shared-ui/components/ui/grid'
import { Header } from '@shared-ui/components/ui/header'
import { Loader } from '@shared-ui/components/ui/loader'
import { ScreenWrapper } from '@shared-ui/components/ui/screen-wrappers/screen-wrapper'
import { GText } from '@shared-ui/components/ui/text'
import { useHardwareBackPress } from '@shared-ui/hooks/rn/use-hardware-back-press'
import { usePinKeyboard } from '@shared-ui/hooks/ui/use-pin-keyboard'
import locales from '@shared-ui/screens/cards/pin-setup/index.i18n.json'
import { useAppTheme } from '@shared-ui/theme'
import { type ComponentType, useEffect, useRef, useState } from 'react'
import { View } from 'react-native'
import { useCreateStyles } from './styles'

export type CardPinKeyboardProps = { value: string; onChange: (value: string) => void }

export type ScreenCardsPinSetupDirectProps = {
  KeyboardComponent: ComponentType<CardPinKeyboardProps>
  cardId: string
  onSuccess: () => void
  onReload: () => void
}

export function ScreenCardsPinSetupDirect({
  cardId,
  onSuccess,
  onReload,
  KeyboardComponent,
}: ScreenCardsPinSetupDirectProps) {
  const styles = useCreateStyles()
  const theme = useAppTheme()
  const { t } = useI18N(locales)
  const cards = useAppSelector(selectedCards)
  const card = cards?.find((item) => item.id === cardId)
  const [step, setStep] = useState<0 | 1>(0)
  const [password, setPassword] = useState('')
  const [checkPassword, setCheckPassword] = useState('')
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const submittingRef = useRef(false)
  const activeRef = useRef(true)

  // Ответ установки PIN не должен менять экран после ухода с него.
  useEffect(() => {
    activeRef.current = true
    return () => {
      activeRef.current = false
    }
  }, [])

  // PIN хранится только в локальном состоянии и отправляется без сохранения в store.
  const handleSubmit = useEvent(async () => {
    if (submittingRef.current) return

    if (!/^\d{6}$/.test(password) || !/^\d{6}$/.test(checkPassword)) {
      setError(t('pin-format'))
      return
    }

    if (password !== checkPassword) {
      setError(t('pin-mismatch'))
      setStep(0)
      setPassword('')
      setCheckPassword('')
      return
    }

    submittingRef.current = true
    setIsSubmitting(true)
    setError('')
    try {
      const response = await apiSetCardPin({ routeParams: { id: cardId }, data: { password, checkPassword } })

      if (!activeRef.current) return

      if (response?.status === 'ok' && response.data === null && !Object.keys(response.errors ?? {}).length) {
        setPassword('')
        setCheckPassword('')
        onSuccess()
        return
      }
      const codes = Object.values(response?.errors ?? {}).flat()

      if (codes.includes('PIN_REQUIRES_PROVIDER_PAGE')) {
        setPassword('')
        setCheckPassword('')
        onReload()
        return
      }
      let message = t('pin-failed')

      if (codes.includes('INVALID_PIN_FORMAT')) message = t('pin-format')

      if (codes.includes('PIN_MISMATCH')) message = t('pin-mismatch')

      if (codes.includes('WEAK_PIN')) message = t('pin-weak')

      if (codes.includes('PROVIDER_UNAVAILABLE')) message = t('provider-unavailable')
      setError(message)
      setStep(0)
      setPassword('')
      setCheckPassword('')
    } catch {
      if (activeRef.current) {
        setError(t('pin-failed'))
        setStep(0)
        setPassword('')
        setCheckPassword('')
      }
    } finally {
      submittingRef.current = false

      if (activeRef.current) setIsSubmitting(false)
    }
  })

  const code = step === 0 ? password : checkPassword
  const handleChange = (value: string) => {
    if (submittingRef.current) return

    setError('')
    const next = value.replace(/\D/g, '').slice(0, 6)

    if (step === 0) setPassword(next)
    else setCheckPassword(next)
  }

  usePinKeyboard({ value: code, max: 6, onPress: handleChange })

  // После шести цифр переходим к подтверждению, затем автоматически отправляем PIN.
  useEffect(() => {
    if (code.length !== 6 || isSubmitting || error) return

    const timer = setTimeout(() => {
      if (step === 0) {
        setCheckPassword('')
        setStep(1)
      } else void handleSubmit()
    }, 150)
    return () => clearTimeout(timer)
  }, [code, step, isSubmitting, error, handleSubmit])

  const handleBack = () => {
    if (step === 0 || submittingRef.current) return true

    setStep(0)
    setPassword('')
    setCheckPassword('')
    setError('')
    return false
  }

  useHardwareBackPress(() => !handleBack(), [step])

  return (
    <>
      <Header isBackButton title={t('title')} onBackHandler={handleBack} />
      <ScreenWrapper contentContainerStyle={styles.content}>
        <Column align="center" gap={24}>
          <AppBankCard type={card?.templateId} isNumberVisible={false} />
          <Column align="center" gap={12}>
            <GText size={24} fw={600} align="center">
              {t(step === 0 ? 'pin-new' : 'pin-repeat')}
            </GText>
            <GText size={16} isSecondary align="center">
              {t(step === 0 ? 'pin-hint' : 'pin-repeat-hint')}
            </GText>
          </Column>
          <View style={styles.dots} accessibilityLabel={t(step === 0 ? 'pin-new' : 'pin-repeat')}>
            {Array.from({ length: 6 }, (_, index) => (
              <View key={index} style={[styles.dot, index < code.length && styles.dotActive]} />
            ))}
          </View>
          {Boolean(error) && (
            <GText align="center" style={styles.error}>
              {error}
            </GText>
          )}
          {isSubmitting && <Loader color={theme.COLOR_TEXT_PRIMARY} />}
        </Column>
        <Column pointerEvents={isSubmitting ? 'none' : 'auto'} style={styles.keyboard}>
          <KeyboardComponent value={code} onChange={handleChange} />
        </Column>
      </ScreenWrapper>
    </>
  )
}
